/**
 * Wen ein Bildelement zeigt — Personenzahl und Beschriftung — und die Mengenregeln, die
 * daraus folgen (Schritt 5 aus `PROTOTYP-PLAN.md`, ARCHITEKTUR.md 6.2 und 6.9).
 *
 * ⚠️ Diese Datei wird vom Browser **und** vom Server gelesen, wie `shared/umriss.ts` und
 * `shared/anordnung.ts`. Sie importiert nichts, zieht also keine Node-Typen in den Client.
 *
 * **Ein Ort für die Mengenregeln** (6.9, Muster aus 2.5): Mindestabnahme und Obergrenze
 * stehen hier und nirgends sonst. Der Preis gehört später in dieselbe Datei — im
 * Prototyp steht er ausdrücklich **nicht** darin: gezählt wird, gerechnet nicht
 * (AB § 1.3, PROTOTYP-PLAN Schritt 5). Es erscheint kein Eurobetrag.
 *
 * Warum das überhaupt zwei Zahlen sind, steht in `SPEC.md` unter „Person ≠ Bildelement":
 * Preis, Mindestabnahme und Obergrenze zählen **Personen**, die Reihe bewegt
 * **Bildelemente**. 60 Personen können 35 Bildelemente sein.
 */

/**
 * Mindestabnahme und Obergrenze, in **Personen**.
 *
 * ⚠️ **Die 15 sind nicht bestätigt.** `SPEC.md` und die AB nennen 15 als Mindestabnahme;
 * Annas eigene Shop-Seite schreibt zum heutigen Produkt „Geeignet für **6 bis 15**
 * Figuren" (`TODO.md`, 24.09.2026). Welche Zahl für das **neue** Produkt gilt, weiß nur
 * sie. Solange das offen ist, ist es eine Konstante an einer Stelle — und keine Zahl,
 * die verstreut in Texten steht.
 *
 * Die 60 sind dagegen belegt: das Klärungsgespräch am 21.09. hat die „rund 40" aus
 * AB § 1.3 auf 60 gehoben, und der ganze Prototyp misst genau diesen Fall.
 */
export const MIN_PERSONEN = 15;
export const MAX_PERSONEN = 60;

/**
 * Wie lang eine Beschriftung höchstens ist.
 *
 * ⚠️ **Gesetzt, nicht gemessen.** 40 Zeichen tragen „Oma Erna mit Bruno" mit Reserve;
 * mehr steht auf 390 px ohnehin nicht in der einen Zeile, die neben der Figur frei ist.
 */
export const MAX_BESCHRIFTUNG = 40;

/**
 * Was der Server als Personenzahl **annimmt**.
 *
 * ⚠️ Hier wird abgewiesen und nicht eingesperrt — anders als bei Größe und Versatz
 * (`shared/anordnung.ts`), und das ist Absicht. Dort rutscht ein Regler um ein
 * Hundertstel über die Grenze, und die ganze Reihe deswegen ungespeichert zu lassen wäre
 * der größere Schaden. Eine Personenzahl kommt nicht aus einem Regler: sie ist die Zahl,
 * die in V1 den Preis trägt (6.9). Eine 0 oder eine 2,5 ist kein Rundungsrest, sondern
 * eine falsche Angabe, und die soll nicht still zu einer richtigen werden.
 */
export function istPersonenzahl(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= MAX_PERSONEN
  );
}

/**
 * Eine Personenzahl, wie sie aus der Datenbank **kommt**: ganzzahlig und eingesperrt.
 *
 * Beim Lesen wird eingesperrt statt abgewiesen, dieselbe Richtung wie bei
 * `gelesenerUmriss` und `rundeGroesse`: ein von Hand geänderter Wert in der Datei darf
 * die Zählung verbiegen, aber nicht 60 heile Kacheln mitnehmen.
 */
export function rundePersonenzahl(wert: number): number {
  if (!Number.isFinite(wert)) return 1;
  return Math.min(MAX_PERSONEN, Math.max(1, Math.round(wert)));
}

/**
 * Eine Beschriftung, wie sie gespeichert wird.
 *
 * Eine Zeile, keine Steuerzeichen, gekappt auf `MAX_BESCHRIFTUNG`. `\p{Cf}` nimmt die
 * unsichtbaren Formatzeichen mit — sonst stünde neben einer Figur eine Beschriftung, die
 * anders aussieht, als sie ist.
 *
 * Gekappt wird nach **Zeichen** und nicht nach UTF-16-Einheiten: `slice(0, 40)` zerlegt
 * ein Emoji in seine Hälften, und übrig bliebe ein Ersatzzeichen.
 *
 * Idempotent, wie jede Reinigung in diesem Projekt: zweimal angewendet ändert nichts.
 * Deshalb wird nach dem Kappen noch einmal beschnitten — ein Schnitt genau hinter einem
 * Leerzeichen ließe sonst beim zweiten Durchlauf ein anderes Ergebnis übrig.
 */
export function putzeBeschriftung(value: unknown): string {
  if (typeof value !== "string") return "";
  const eineZeile = value
    .replace(/[\p{Cc}\p{Cf}]/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
  return [...eineZeile].slice(0, MAX_BESCHRIFTUNG).join("").trim();
}

/** Die beiden Zahlen, die laufend nebeneinander stehen (`SPEC.md`, Schritt 5). */
export interface Zaehlung {
  personen: number;
  bildelemente: number;
}

/**
 * Beide Zahlen aus den Bildelementen.
 *
 * **Getrennt geführt, nicht auseinander abgeleitet.** Die Versuchung wäre, die
 * Personenzahl aus der Anzahl der Elemente zu schätzen; genau das verbietet `SPEC.md`,
 * und genau daran hängt das Abnahmekriterium von Schritt 5 („60 Personen aus weniger als
 * 60 Bildelementen").
 */
export function zaehlung(elemente: readonly { personen: number }[]): Zaehlung {
  let personen = 0;
  for (const element of elemente) personen += element.personen;
  return { personen, bildelemente: elemente.length };
}

/**
 * Wo die Collage zwischen Mindestabnahme und Obergrenze steht.
 *
 * Die Regel steht hier und der Satz dazu in der Oberfläche: der Zähler ist „die
 * Rückmeldung, die eine Fehlbestellung verhindert" (6.2), und eine Grenze, die an zwei
 * Orten gerechnet wird, läuft irgendwann auseinander (2.5).
 */
export type Mengenstand = "zu wenig" | "passt" | "zu viel";

export function mengenstand(personen: number): Mengenstand {
  if (personen < MIN_PERSONEN) return "zu wenig";
  if (personen > MAX_PERSONEN) return "zu viel";
  return "passt";
}
